import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Filter from '@mulesoft/anypoint-components/lib/Filter';
import styles from './ScanConfigurationSearch.css';
import { ScannerStatusEnum } from '../../types/commonTypes';
import { getTargetSystems } from '~/services/agentScannerService';

// { key: 'Running', value: 'Running' },
const filterOptions = (() => {
  const options = [];

  for (const scannerStatusInfo of Object.values(ScannerStatusEnum)) {
    // Don't include UNKNOWN status in the filter options
    if (scannerStatusInfo.key !== 'unknown') {
      options.push({
        key: scannerStatusInfo.key,
        value: scannerStatusInfo.label
      });
    }
  }

  return options;
})();

const ScanConfigurationSearch = ({
  onSearch = () => {},
  onClear = () => {},
  onFilterStatusChange = () => {},
  onFilterTargetChange = () => {},
  searchTerm = '',
  selectedFilterStatus = null,
  selectedFilterTarget = null,
  maxLength = 100,
  context,
  organizationId
}) => {
  // Track previous searchTerm prop to detect actual prop changes
  const prevSearchTermRef = useRef(searchTerm);
  // Track previous filter values to detect when they're cleared
  const prevSelectedStatusFilterRef = useRef(selectedFilterStatus);
  const prevSelectedTargetFilterRef = useRef(selectedFilterTarget);

  // Local state for the input value - initialize with searchTerm prop
  const [value, setValue] = useState(searchTerm);

  // Key to force Filter re-render when clearing - use a single timestamp-based key
  const [filtersKey, setFiltersKey] = useState(Date.now());

  // State for target system options
  const [targetSystemOptions, setTargetSystemOptions] = useState([]);
  const [isLoadingTargetSystems, setIsLoadingTargetSystems] = useState(false);
  const [targetSystemsError, setTargetSystemsError] = useState(null);

  // Sync value state when searchTerm prop actually changes from parent
  // This mimics getDerivedStateFromProps behavior
  useEffect(() => {
    // Only update if the prop actually changed (not just a re-render)
    if (searchTerm !== prevSearchTermRef.current) {
      setValue(searchTerm);
      prevSearchTermRef.current = searchTerm;
    }
  }, [searchTerm]);

  // Fetch target systems on mount
  useEffect(() => {
    if (!context) {
      return;
    }

    setIsLoadingTargetSystems(true);
    setTargetSystemsError(null);

    getTargetSystems({ organizationId, context })
      .then((response) => {
        // Map API response to Filter options format
        // Using id as key/value and name as label
        const options = (response || []).map((system) => ({
          key: system.id,
          value: system.name
        }));

        setTargetSystemOptions(options);
      })
      .catch((error) => {
        console.error('Error fetching target systems:', error);
        setTargetSystemsError(error);
        setTargetSystemOptions([]);
      })
      .finally(() => {
        setIsLoadingTargetSystems(false);
      });
  }, [context, organizationId]);

  // Update filters key when both filters are cleared (transition from having values to null)
  useEffect(() => {
    const wasFilterSelected = prevSelectedStatusFilterRef.current !== null;
    const isFilterCleared = selectedFilterStatus === null;
    const wasTargetSelected = prevSelectedTargetFilterRef.current !== null;
    const isTargetCleared = selectedFilterTarget === null;

    // Only update key if at least one filter was cleared (transition from value to null)
    // and both are now cleared
    if (
      (wasFilterSelected || wasTargetSelected) &&
      isFilterCleared &&
      isTargetCleared
    ) {
      setFiltersKey(Date.now());
    }

    // Update refs for next comparison
    prevSelectedStatusFilterRef.current = selectedFilterStatus;
    prevSelectedTargetFilterRef.current = selectedFilterTarget;
  }, [selectedFilterStatus, selectedFilterTarget]);

  const handleFilterStatusChange = useCallback(
    ({ value: selectedOption }) => {
      const newFilter = selectedOption ? selectedOption.key : null;

      onFilterStatusChange(newFilter);
    },
    [onFilterStatusChange]
  );

  const handleFilterTargetChange = useCallback(
    ({ value: selectedOption }) => {
      const newFilter = selectedOption ? selectedOption.key : null;

      onFilterTargetChange(newFilter);
    },
    [onFilterTargetChange]
  );

  const handleClearStatusFilters = useCallback(() => {
    onFilterStatusChange(null);
  }, [onFilterStatusChange]);

  const handleClearTargetFilters = useCallback(() => {
    onFilterTargetChange(null);
  }, [onFilterTargetChange]);

  const handleClearAll = useCallback(() => {
    setValue('');
    onClear();
    // Clear the filters by calling the Filter's onClear handlers
    // The useEffect will detect when both filters are cleared and update the key
    handleClearStatusFilters();
    handleClearTargetFilters();
  }, [onClear, handleClearStatusFilters, handleClearTargetFilters]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      onSearch(value.trim());
    },
    [value, onSearch]
  );

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleSubmit(event);
      }
    },
    [handleSubmit]
  );

  const handleTextChange = useCallback(
    ({ value: newValue, isClear }) => {
      setValue(newValue);
      if (isClear) {
        onClear();
      }
    },
    [onClear]
  );

  // Find selected option for status filter
  const selectedStatusOption = useMemo(() => {
    return selectedFilterStatus
      ? filterOptions.find((option) => option.key === selectedFilterStatus)
      : undefined;
  }, [selectedFilterStatus]);

  // Find selected option for agent provider filter
  const selectedTargetOption = useMemo(() => {
    return selectedFilterTarget
      ? targetSystemOptions.find(
          (option) => option.key === selectedFilterTarget
        )
      : undefined;
  }, [selectedFilterTarget, targetSystemOptions]);

  return (
    <div className={styles.searchContainer}>
      <form
        data-test-id="agent-scanners-search-form"
        className={styles.searchForm}
        onSubmit={handleSubmit}
      >
        <div className={styles.searchInputWrapper}>
          <TextField
            id="agent-scanners-search"
            aria-label="Search agent scanners"
            className={styles.searchInput}
            testId="agent-scanners-search-bar"
            name="search"
            type="search"
            placeholder="Search by scanner name and provider…"
            value={value}
            maxLength={maxLength}
            onChange={handleTextChange}
            onKeyPress={handleKeyPress}
          />
          <Button
            kind="tertiary"
            neutral
            onClick={handleClearAll}
            testId="clear-all-button"
            aria-label="Clear All Filters"
            className={styles.clearButton}
          >
            Clear All Filters
          </Button>
        </div>
        <div className={styles.filterSection} key={filtersKey}>
          <Filter
            testId="status-filter"
            checkboxId="status-filter"
            className={styles.filter}
            aria-label="Filter by status"
            options={filterOptions}
            placeholder="All Statuses"
            showClearButton={!!selectedFilterStatus}
            selectedOption={selectedStatusOption}
            onOptionChange={handleFilterStatusChange}
            onClear={handleClearStatusFilters}
          />
          <Filter
            testId="target-filter"
            checkboxId="target-filter"
            className={styles.filter}
            aria-label="Filter by agent provider"
            options={targetSystemOptions}
            placeholder="All Providers"
            showClearButton={!!selectedFilterTarget}
            selectedOption={selectedTargetOption}
            onOptionChange={handleFilterTargetChange}
            onClear={handleClearTargetFilters}
            disabled={isLoadingTargetSystems || !!targetSystemsError}
          />
        </div>
      </form>
    </div>
  );
};

ScanConfigurationSearch.propTypes = {
  onSearch: PropTypes.func,
  onClear: PropTypes.func,
  onFilterStatusChange: PropTypes.func,
  onFilterTargetChange: PropTypes.func,
  searchTerm: PropTypes.string,
  selectedFilterStatus: PropTypes.string,
  selectedFilterTarget: PropTypes.string,
  maxLength: PropTypes.number,
  context: PropTypes.object,
  organizationId: PropTypes.string
};

export default ScanConfigurationSearch;
